/** Tenant Backoffice ↔ Canvas last-location memory
 * (VISTOERA_CANVAS_TO_TENANT_BACKOFFICE_RETURN_STATE_RESTORE_SKELETON_FLASH_ELIMINATION_AND_LAST_BACKOFFICE_LOCATION_MEMORY_P0_V1).
 *
 * Workspace-scoped, `sessionStorage`-only navigation-continuity state —
 * deliberately a SEPARATE store from `../navigation/canvasNavigationContext.ts`'s
 * Canvas-project return context (task spec §41/§42: reuse the same
 * "small client-side persistence" architectural idea, but never merge
 * Canvas viewport/project state with Backoffice route state into one
 * object — they have different owners, different lifecycles, and
 * different validation rules).
 *
 * Lifecycle (spec §43):
 * - Backoffice pages normally live their life; every canonical
 *   "open/resume Canvas" call site (`openCanvasFromWorkspace` in
 *   `canvasWorkspaceTransition.tsx`) calls `freezeCurrentTenantBackofficeLocation()`
 *   right before it navigates INTO Canvas — this is the ONLY place this
 *   module ever WRITES (spec §26/§105-111: on the "leaving Backoffice"
 *   commit, never continuously, never on a scroll listener).
 * - While Canvas is open, this state is frozen — untouched.
 * - `CanvasBrandEntry`'s V logo calls `resolveTenantBackofficeReturnLocation`
 *   to compute exactly ONE navigation target (spec §75-80 — never
 *   Overview-first-then-redirect).
 */

export interface TenantBackofficeLocation {
  pathname: string
  search: string
  hash: string
  scrollY: number
  savedAt: number
}

export interface ResolvedTenantBackofficeReturn {
  pathname: string
  search: string
  hash: string
  scrollY: number
  /** True when no valid saved location existed and the canonical
   * Overview fallback (spec §7/§87/§125) was used instead. */
  isFallback: boolean
}

const STORAGE_PREFIX = 'vistoera.backoffice.return.v1'

function storageKey(workspaceId: string): string {
  return `${STORAGE_PREFIX}.${workspaceId}`
}

/** Canonical Overview route — same shape as
 * `canvasNavigationContext.ts`'s `workspaceOverviewRoute` (task spec §7:
 * "不要根据本提示词猜 route" — this IS the real Router's
 * `<Route path="overview" element={<WorkspaceOverviewPage/>}/>` under
 * `<Route path=":workspaceId" element={<WorkspaceLayout/>}>` in
 * `WorkspaceApp.tsx`, forensically confirmed, not guessed). */
export function workspaceOverviewRoute(workspaceId: string): string {
  return `/workspace/${workspaceId}/overview`
}

/** Canonical Tenant Backoffice route classifier (spec §31-§33). A route
 * is a Tenant Backoffice route iff it is scoped under exactly
 * `/workspace/:workspaceId/...` AND is not one of the Canvas surfaces
 * nested under that same prefix. Mirrors `onProductionCanvas` in
 * `WorkspaceLayout.tsx` byte-for-byte on purpose so the two can never
 * drift apart (both decide "is this the fullscreen Canvas shell or the
 * Sidebar+Header Backoffice shell?").
 *
 * Deliberately NOT `pathname.includes('workspace')` (spec §32) — Canvas
 * routes are ALSO nested under `/workspace/:workspaceId/...`, so a naive
 * substring check would wrongly classify Canvas as Backoffice. */
export function isTenantBackofficeRoute(pathname: string, workspaceId: string): boolean {
  if (!workspaceId || typeof pathname !== 'string') return false
  const prefix = `/workspace/${workspaceId}`
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) return false
  const rest = pathname.slice(prefix.length)
  if (/^\/content-production\/[^/]+\/canvas(?:\/|$)/.test(rest)) return false
  if (/^\/home(?:\/|$)/.test(rest)) return false
  return true
}

/** Defense-in-depth structural validation for values read back OUT of
 * `sessionStorage` (spec §39/§174-181 — "saved route 是 untrusted client
 * state", re-validated at READ time regardless of how it got written).
 * Rejects anything that is not a plain same-origin relative path —
 * `javascript:`, absolute `https://...`, and protocol-relative `//host`
 * strings all fail this check before `isTenantBackofficeRoute` even
 * runs its workspace-scope comparison. */
function isSafeInternalPathnameFragment(value: string): boolean {
  if (typeof value !== 'string') return false
  if (value.startsWith('//')) return false
  if (/[:\\]/.test(value)) return false
  if (/\s/.test(value)) return false
  return true
}

function isSafePathname(pathname: string): boolean {
  return typeof pathname === 'string' && pathname.startsWith('/') && isSafeInternalPathnameFragment(pathname)
}

function isSafeSearch(search: string): boolean {
  if (search === '') return true
  return typeof search === 'string' && search.startsWith('?') && isSafeInternalPathnameFragment(search)
}

function isSafeHash(hash: string): boolean {
  if (hash === '') return true
  return typeof hash === 'string' && hash.startsWith('#') && isSafeInternalPathnameFragment(hash)
}

function readRaw(workspaceId: string): TenantBackofficeLocation | null {
  if (!workspaceId || typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(storageKey(workspaceId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<TenantBackofficeLocation>
    if (!parsed || typeof parsed.pathname !== 'string') return null
    return {
      pathname: parsed.pathname,
      search: typeof parsed.search === 'string' ? parsed.search : '',
      hash: typeof parsed.hash === 'string' ? parsed.hash : '',
      scrollY: typeof parsed.scrollY === 'number' && Number.isFinite(parsed.scrollY) ? Math.max(0, parsed.scrollY) : 0,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
    }
  } catch {
    return null
  }
}

function writeRaw(workspaceId: string, location: TenantBackofficeLocation): void {
  if (!workspaceId || typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(storageKey(workspaceId), JSON.stringify(location))
  } catch {
    /* Quota / privacy mode — silently degrade to Overview fallback. */
  }
}

/** Persists the CURRENT route (pathname/search/hash — spec §14 "Route
 * 是 P0") for this workspace. Only ever called from
 * `freezeCurrentTenantBackofficeLocation` below — never on a per-render
 * or per-route-change basis elsewhere, so this is effectively "write
 * once, right before leaving to Canvas" (spec §107). Silently no-ops if
 * `location.pathname` is not itself a valid Tenant Backoffice route
 * (spec §27/§28/§29/§30 — never record Canvas/Platform Admin/Login/
 * Error surfaces as the "last healthy Backoffice page"). */
function persistTenantBackofficeRoute(
  workspaceId: string,
  location: { pathname: string; search: string; hash: string },
): void {
  if (!isTenantBackofficeRoute(location.pathname, workspaceId)) return
  writeRaw(workspaceId, {
    pathname: location.pathname,
    search: location.search || '',
    hash: location.hash || '',
    scrollY: 0,
    savedAt: Date.now(),
  })
}

/** Reads the current vertical scroll owner's position exactly once
 * (spec §15/§17/§18 forensic: the Tenant Backoffice's real scroll owner
 * is the document/window itself — `WorkspaceLayout.tsx`'s `.tc-content`
 * and every nested page use `overflow: visible`, no inner scroll
 * container — confirmed live on Preview: `window.scrollTo` moves
 * `window.scrollY`, no inner element ever has `scrollHeight >
 * clientHeight` while `overflow-y` is `auto`/`scroll`). Sidebar's own
 * independent scroll (`.tc-sider` — genuinely `overflow-y: auto`) is
 * deliberately NOT this value (spec §19). */
function readCurrentScrollY(): number {
  if (typeof window === 'undefined') return 0
  const y = window.scrollY ?? document.documentElement.scrollTop ?? document.body.scrollTop ?? 0
  return Number.isFinite(y) ? Math.max(0, y) : 0
}

/** Canonical "leaving Tenant Backoffice for Canvas" hook (spec §26/§43
 * "freeze 当前 route/query/scroll"). Called from
 * `openCanvasFromWorkspace` — the single choke point every canonical
 * Workspace→Canvas entry (`SaasBacksideBrandLogo`, `ContentProductionListPage`
 * project cards, `ContentProductionWorkbenchPage`'s "生产画布" button — spec
 * §70 Entry Map) already funnels through, so this never needs to be
 * wired into more than one place. Reads `window.location` directly
 * (rather than taking React Router's `useLocation()` as a parameter)
 * so it stays a plain, framework-agnostic function callable from a
 * non-component helper. */
export function freezeCurrentTenantBackofficeLocation(): void {
  if (typeof window === 'undefined') return
  const match = window.location.pathname.match(/^\/workspace\/([^/]+)/)
  if (!match) return
  const workspaceId = match[1]
  const current = { pathname: window.location.pathname, search: window.location.search, hash: window.location.hash }
  if (!isTenantBackofficeRoute(current.pathname, workspaceId)) return
  persistTenantBackofficeRoute(workspaceId, current)
  const scrollY = readCurrentScrollY()
  if (scrollY > 0) {
    const stored = readRaw(workspaceId)
    if (stored && stored.pathname === current.pathname) {
      writeRaw(workspaceId, { ...stored, scrollY })
    }
  }
}

/** Canonical Canvas → Tenant Backoffice return resolver (spec §43/§121
 * `resolveTenantBackofficeReturnLocation`). Re-validates the saved
 * value at READ time (never trusts write-time validation alone — spec
 * §174-181): workspace scope, Tenant-Backoffice-ness, and safe-internal-
 * path shape are all re-checked here. Falls back to Overview (spec §7/
 * §38/§87/§125) on any missing/invalid/cross-workspace/external/Canvas
 * value — this is the ONLY function `CanvasBrandEntry` needs to compute
 * its single `navigate()` target (spec §78 "必须一次 navigate"). */
export function resolveTenantBackofficeReturnLocation(workspaceId: string): ResolvedTenantBackofficeReturn {
  const fallback: ResolvedTenantBackofficeReturn = {
    pathname: workspaceOverviewRoute(workspaceId),
    search: '',
    hash: '',
    scrollY: 0,
    isFallback: true,
  }
  if (!workspaceId) return fallback
  const stored = readRaw(workspaceId)
  if (!stored) return fallback
  if (!isSafePathname(stored.pathname)) return fallback
  if (!isSafeSearch(stored.search)) return fallback
  if (!isSafeHash(stored.hash)) return fallback
  if (!isTenantBackofficeRoute(stored.pathname, workspaceId)) return fallback
  return { pathname: stored.pathname, search: stored.search, hash: stored.hash, scrollY: stored.scrollY, isFallback: false }
}

export function tenantBackofficeReturnPath(resolved: ResolvedTenantBackofficeReturn): string {
  return `${resolved.pathname}${resolved.search}${resolved.hash}`
}

/** Test-only escape hatch (spec §136 — simulate "no Backoffice history
 * at all" even when a real one exists, to exercise the first-visit
 * fallback deterministically) — also useful for real users' very first
 * session (nothing to clear, `resolveTenantBackofficeReturnLocation`
 * already falls back correctly on its own). */
export function clearTenantBackofficeReturnLocation(workspaceId: string): void {
  if (!workspaceId || typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(storageKey(workspaceId))
  } catch {
    /* ignore */
  }
}
